'use client';

import Head from 'next/head';
import Script from 'next/script';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { COOKIE_LAW_DOMAIN_SCRIPT } from '@/core/constants/env';
import useLocaleParams from '@/ui/hooks/use-locale-params';

type OneTrustStatus = 'granted' | 'denied' | 'pending';

type OneTrustStatuses = {
  ga4: OneTrustStatus;
};

type OneTrustContextType = {
  status: OneTrustStatuses | null;
};

const OneTrustContext = createContext<OneTrustContextType | null>(null);

declare global {
  interface Window {
    onOneTrustLoaded?: () => void;
    onOneTrustConsentChanged?: () => void;
    OneTrustActiveGroups?: string[];
    OneTrust?: {
      ToggleInfoDisplay: () => void;
      GetDomainData: () => {
        GoogleConsent?: {
          GCAdUserData: string;
          GCAnalyticsStorage: string;
          GCEnable: boolean;
          GCFunctionalityStorage: string;
          GCPersonalizationStorage: string;
          GCRedactEnable: boolean;
          GCSecurityStorage: string;
          GCWaitTime: number;
        };
      };
    };
  }
}

export const OneTrustProvider = ({ children }: PropsWithChildren) => {
  const { language = 'en' } = useLocaleParams();

  const [status, setStatus] = useState<OneTrustStatuses>({
    ga4: 'pending',
  });

  const value = useMemo(
    () => ({
      status,
    }),
    [status],
  );

  useLayoutEffect(() => {
    const updateStatus = () => {
      if (window.OneTrust) {
        const gaGroup =
          window.OneTrust.GetDomainData().GoogleConsent?.GCAnalyticsStorage;
        const activeGroups = window.OneTrustActiveGroups;
        setStatus({
          ga4:
            gaGroup && activeGroups?.includes(gaGroup) ? 'granted' : 'denied',
        });
      }
    };

    window.onOneTrustLoaded = updateStatus;
    window.onOneTrustConsentChanged = updateStatus;
  }, []);

  return (
    <OneTrustContext.Provider value={value}>
      <Head>
        <link rel="preconnect" href="https://cdn.cookielaw.org" />
      </Head>

      <Script
        id="onetrust-sdk"
        strategy="afterInteractive"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        type="text/javascript"
        data-domain-script={COOKIE_LAW_DOMAIN_SCRIPT}
      />

      <Script
        id="onetrust-wrapper"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            function OptanonWrapper() {
              if (window.OneTrust) {
              window.OneTrust.changeLanguage('${language}');
              window.OneTrust.OnConsentChanged(window.onOneTrustConsentChanged);
              window.onOneTrustLoaded();

                if (window.dataLayer) {
                  window.dataLayer.push({
                    'event': 'onetrust_consent_ready',
                    'locale': '${language}'
                  });
                }
              }
            }
              `,
        }}
      />

      {children}
    </OneTrustContext.Provider>
  );
};

export const useOneTrust = (): OneTrustContextType => {
  const context = useContext(OneTrustContext);
  if (!context) {
    throw new Error('useOneTrust must be used within a OneTrustProvider');
  }
  return context;
};

export default OneTrustProvider;
